import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders, getSessionHeaders } from "@/lib/session";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus,
  Trash2,
  Sparkles,
  Loader2,
  ArrowLeft,
  Play,
  Table2,
  ChevronDown,
  ChevronRight,
  Columns,
  Download,
  FileText,
  RefreshCw,
  AlertCircle,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";
import SqlTextarea from "./SqlTextarea";
import { validateSqlAgainstSchema, type SchemaWarning } from "@/lib/sql-schema-validator";
import { useAuth } from "@/hooks/useAuth";
import { useDownloadLimit } from "@/hooks/useDownloadLimit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import PlaceholderInput from "./PlaceholderInput";
import {
  extractPlaceholders,
  inferPlaceholderInfo,
  replacePlaceholders,
  type PlaceholderInfo,
} from "@/lib/query-placeholders";

interface FilterRow {
  column: string;
  operator: string;
  value: string;
}

interface JoinRow {
  table: string;
  type: "INNER JOIN" | "LEFT JOIN" | "RIGHT JOIN" | "FULL JOIN";
  leftColumn: string;
  rightColumn: string;
}

interface ColumnEntry {
  expression: string; // column name or custom expression
  alias: string;
  aggregate: "" | "COUNT" | "SUM" | "AVG" | "MIN" | "MAX";
}

interface OrderEntry {
  column: string;
  direction: "ASC" | "DESC";
}

interface BuilderConfig {
  table: string;
  columns: ColumnEntry[];
  joins: JoinRow[];
  filters: FilterRow[];
  groupBy: string[];
  orderBy: OrderEntry[];
  limit: string;
}

const defaultBuilder: BuilderConfig = {
  table: "",
  columns: [],
  joins: [],
  filters: [],
  groupBy: [],
  orderBy: [],
  limit: "100",
};

const OPERATORS = ["=", "!=", ">", "<", ">=", "<=", "LIKE", "IN", "IS NULL", "IS NOT NULL"];
const JOIN_TYPES: JoinRow["type"][] = ["INNER JOIN", "LEFT JOIN", "RIGHT JOIN", "FULL JOIN"];
const AGGREGATES: ColumnEntry["aggregate"][] = ["", "COUNT", "SUM", "AVG", "MIN", "MAX"];

function buildSql(config: BuilderConfig): string {
  if (!config.table) return "";

  // Build SELECT columns
  let colParts: string[] = [];
  if (config.columns.length === 0) {
    colParts = ["*"];
  } else {
    colParts = config.columns.map((c) => {
      let expr = c.expression || "*";
      if (c.aggregate) expr = `${c.aggregate}(${expr})`;
      if (c.alias) expr += ` AS ${c.alias}`;
      return expr;
    });
  }

  let sql = `SELECT ${colParts.join(", ")}\nFROM ${config.table}`;

  // Joins
  for (const j of config.joins) {
    if (j.table && j.leftColumn && j.rightColumn) {
      sql += `\n${j.type} ${j.table} ON ${j.leftColumn} = ${j.rightColumn}`;
    }
  }

  // Filters
  const validFilters = config.filters.filter((f) => f.column);
  if (validFilters.length > 0) {
    const where = validFilters
      .map((f) => {
        if (f.operator === "IS NULL" || f.operator === "IS NOT NULL") return `${f.column} ${f.operator}`;
        return `${f.column} ${f.operator} '${f.value}'`;
      })
      .join(" AND ");
    sql += `\nWHERE ${where}`;
  }

  // Group by
  if (config.groupBy.length > 0) {
    sql += `\nGROUP BY ${config.groupBy.join(", ")}`;
  }

  // Order by
  const validOrders = config.orderBy.filter((o) => o.column);
  if (validOrders.length > 0) {
    sql += `\nORDER BY ${validOrders.map((o) => `${o.column} ${o.direction}`).join(", ")}`;
  }

  if (config.limit) {
    sql += `\nLIMIT ${config.limit}`;
  }
  return sql;
}

/** Wrap query with LIMIT 1000 for test mode if no limit present */
function wrapTestSql(sql: string): string {
  if (!sql.trim()) return sql;
  const upper = sql.toUpperCase();
  if (upper.includes("LIMIT")) {
    // Replace existing limit if > 1000
    return sql.replace(/LIMIT\s+(\d+)/i, (_match, num) => {
      return `LIMIT ${Math.min(parseInt(num, 10), 1000)}`;
    });
  }
  return sql.trimEnd() + "\nLIMIT 1000";
}

interface Props {
  onClose: () => void;
  /** Called after a successful save/update with the persisted query record. */
  onSaved?: (query: { id: string; name: string; sql_query: string }) => void;
  editingQuery: any | null;
  schemaTables?: string[];
  schemaColumns?: Record<string, string[]>;
  autoRun?: boolean;
  linkType?: string;
  linkId?: string;
  /** When false, skip persisting to saved_queries and just return the SQL via onSaved. */
  saveToDb?: boolean;
}

const DRAFT_KEY = "query-editor-draft";

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDraft(data: any) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
  } catch {}
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {}
}

const QueryEditor = ({
  onClose,
  onSaved,
  editingQuery,
  schemaTables: propTables,
  schemaColumns: propColumns,
  autoRun,
  linkType,
  linkId,
  saveToDb = true,
}: Props) => {
  const { profile } = useAuth();
  const downloadAllowed = profile?.download_allowed ?? true;
  const { remaining: downloadRemaining, dailyLimit, todayUsed, checkAndLog } = useDownloadLimit();

  // Normalize builder to ensure all new fields exist
  const normalizeBuilder = (b: any): BuilderConfig => ({
    ...defaultBuilder,
    ...b,
    joins: Array.isArray(b?.joins) ? b.joins : [],
    columns: Array.isArray(b?.columns)
      ? b.columns.map((c: any) => (typeof c === "string" ? { expression: c, alias: "", aggregate: "" as const } : c))
      : [],
    groupBy: Array.isArray(b?.groupBy) ? b.groupBy : [],
    orderBy: Array.isArray(b?.orderBy) ? b.orderBy : [],
  });

  const queryClient = useQueryClient();
  const draft = editingQuery ? null : loadDraft();
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const {
    schemaData,
    tables: hookTables,
    columns: hookColumns,
    isLoading: schemaLoading,
    refetch: refetchSchema,
  } = useMcpSchema();
  const schemaTables = propTables && propTables.length > 0 ? propTables : hookTables;
  const schemaColumns = propColumns && Object.keys(propColumns).length > 0 ? propColumns : hookColumns;
  const [tab, setTab] = useState(draft?.tab || "ai");
  const [name, setName] = useState(draft?.name || "");
  const [connectionId, setConnectionId] = useState(draft?.connectionId || "");
  const [builder, setBuilder] = useState<BuilderConfig>(normalizeBuilder(draft?.builder));
  const [rawSql, setRawSql] = useState(draft?.rawSql || "");
  const [aiPrompt, setAiPrompt] = useState(draft?.aiPrompt || "");
  const [aiSql, setAiSql] = useState(draft?.aiSql || "");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isReport, setIsReport] = useState(!!editingQuery?.is_report);

  // Placeholder state
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});

  // Test state
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<any[] | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testColumns, setTestColumns] = useState<string[]>([]);
  const [fixingQuery, setFixingQuery] = useState(false);

  // Fetch database connections
  const { data: connections = [] } = useQuery({
    queryKey: ["database-connections-for-queries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pipeline_source_connector")
        .select("id, name, enabled")
        .eq("enabled", true);
      if (error) throw error;
      return data || [];
    },
  });

  // Use first enabled connection by default
  useEffect(() => {
    if (!connectionId && connections.length > 0) {
      setConnectionId(connections[0].id);
    }
  }, [connections, connectionId]);

  // Use mcp-api schema if available, fallback to connection schema
  const selectedSchema = useMemo(() => {
    if (schemaTables && schemaTables.length > 0 && schemaColumns) {
      return { tables: schemaTables, columns: schemaColumns };
    }
    return { tables: [] as string[], columns: {} as Record<string, string[]> };
  }, [connectionId, connections, schemaTables, schemaColumns]);

  // Populate on edit
  useEffect(() => {
    if (editingQuery) {
      setName(editingQuery.name || "");
      setConnectionId(editingQuery.database_connection_id || "");
      setTab("raw");
      setRawSql(editingQuery.sql_query || "");
      setAiSql(editingQuery.generation_mode === "ai" ? editingQuery.sql_query : "");
      setAiPrompt("");
      setIsReport(!!editingQuery.is_report);
      if (editingQuery.builder_config && editingQuery.generation_mode === "builder") {
        setBuilder(normalizeBuilder(editingQuery.builder_config));
      } else {
        setBuilder(defaultBuilder);
      }
    } else {
      setName("");
      setConnectionId("");
      setTab("raw");
      setBuilder(defaultBuilder);
      setRawSql("");
      setAiPrompt("");
      setAiSql("");
      setIsReport(false);
    }
    setTestResults(null);
    setTestError(null);
  }, [editingQuery]);

  // Persist draft for new queries
  useEffect(() => {
    if (!editingQuery) {
      saveDraft({ tab, name, connectionId, builder, rawSql, aiPrompt, aiSql });
    }
  }, [tab, name, connectionId, builder, rawSql, aiPrompt, aiSql, editingQuery]);

  const finalSql = tab === "builder" ? buildSql(builder) : tab === "ai" ? aiSql : rawSql;

  // Validate SQL against schema
  const schemaWarnings = useMemo(
    () => validateSqlAgainstSchema(finalSql, schemaTables, schemaColumns),
    [finalSql, schemaTables, schemaColumns],
  );

  // Detect placeholders and infer their types from schema
  const placeholderNames = useMemo(() => extractPlaceholders(finalSql), [finalSql]);
  const placeholderInfos: PlaceholderInfo[] = useMemo(
    () => placeholderNames.map((name) => inferPlaceholderInfo(finalSql, name, schemaData)),
    [placeholderNames, finalSql, schemaData],
  );

  // Reset placeholder values when placeholder set changes, applying defaults
  useEffect(() => {
    setPlaceholderValues((prev) => {
      const next: Record<string, string> = {};
      for (const ph of placeholderInfos) {
        next[ph.name] = prev[ph.name] ?? ph.defaultValue ?? "";
      }
      return next;
    });
  }, [placeholderNames.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Enter a prompt to generate SQL");
      return;
    }
    setGenerating(true);
    try {
      // Build schema context from fetched MCP schema
      const schemaContext =
        schemaTables.length > 0 && schemaColumns
          ? schemaTables.map((t) => `${t}: ${(schemaColumns[t] || []).join(", ")}`).join("\n")
          : "";
      const { data, error } = await supabase.functions.invoke("generate-query", {
        headers: await getSessionHeaders(),
        body: { prompt: aiPrompt, connection_id: connectionId || null, schema_context: schemaContext },
      });
      if (error) throw error;
      setAiSql(data?.sql || "");
    } catch (e: any) {
      toast.error(e.message || "Failed to generate query");
    } finally {
      setGenerating(false);
    }
  };

  const handleFixQuery = async () => {
    if (schemaWarnings.length === 0 || !finalSql.trim()) return;
    setFixingQuery(true);
    try {
      const schemaContext =
        schemaTables.length > 0 && schemaColumns
          ? schemaTables.map((t) => `${t}: ${(schemaColumns[t] || []).join(", ")}`).join("\n")
          : "";
      const warningsSummary = schemaWarnings
        .map((w) => `${w.type.toUpperCase()}: ${w.message}`)
        .join("\n");
      const fixPrompt = `Fix the following SQL query. The query has schema issues that need to be corrected.

Original SQL:
${finalSql}

Issues found:
${warningsSummary}

Replace any incorrect column or table names with the closest matching ones from the schema. Fix any syntax issues. Return only the corrected SQL query.`;

      const { data, error } = await supabase.functions.invoke("generate-query", {
        headers: await getSessionHeaders(),
        body: { prompt: fixPrompt, connection_id: connectionId || null, schema_context: schemaContext },
      });
      if (error) throw error;
      const fixedSql = data?.sql || "";
      if (fixedSql) {
        if (tab === "ai") {
          setAiSql(fixedSql);
        } else {
          setRawSql(fixedSql);
        }
        toast.success("Query fixed — review the changes");
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to fix query");
    } finally {
      setFixingQuery(false);
    }
  };

  const handleTest = async () => {
    if (!finalSql.trim()) {
      toast.error("No SQL to test");
      return;
    }

    // Validate that all placeholders have values
    if (placeholderNames.length > 0) {
      const missing = placeholderNames.filter((n) => !placeholderValues[n]?.trim());
      if (missing.length > 0) {
        toast.error(`Please fill in: ${missing.join(", ")}`);
        return;
      }
    }

    setTesting(true);
    setTestResults(null);
    setTestError(null);
    setTestColumns([]);

    try {
      // Replace placeholders with user-supplied values before executing
      const resolvedSql =
        placeholderNames.length > 0 ? replacePlaceholders(finalSql, placeholderValues, placeholderInfos) : finalSql;
      const testSql = wrapTestSql(resolvedSql);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
        method: "POST",
        headers,
        body: JSON.stringify([testSql]),
      });

      const rawText = await res.text();
      let data: any;
      try {
        data = JSON.parse(rawText);
      } catch {
        throw new Error(rawText || `Request failed with status ${res.status}`);
      }

      if (!res.ok) {
        // Extract error from various response shapes
        const errMsg = data?.error || data?.detail || data?.message || JSON.stringify(data);
        throw new Error(errMsg);
      }

      // Check for error in 200 response body (backend may return SQL errors as 200)
      const extractError = (d: any): string | null => {
        if (!d) return null;
        if (typeof d === "object" && !Array.isArray(d)) {
          if (d.error) return typeof d.error === "string" ? d.error : JSON.stringify(d.error);
          if (d.detail) return typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail);
          if (d.message && !d.rows && !d.results && !d.result) return d.message;
        }
        if (Array.isArray(d)) {
          const first = d[0];
          if (first && typeof first === "object" && !Array.isArray(first)) {
            if (first.error) return typeof first.error === "string" ? first.error : JSON.stringify(first.error);
          }
        }
        return null;
      };
      const bodyError = extractError(data);
      if (bodyError) {
        throw new Error(bodyError);
      }

      // data may be { results: [[ {…}, {…} ]] } or an array directly
      let rows: any[] = [];
      if (Array.isArray(data)) {
        // Direct array or wrapped: [ [{row}, ...] ]
        const first = data[0];
        rows = Array.isArray(first) ? first : first?.rows || first?.result || data;
      } else if (data?.results) {
        const first = Array.isArray(data.results) ? data.results[0] : data.results;
        // first could be an array of rows directly, or an object with .rows/.result
        rows = Array.isArray(first) ? first : first?.rows || first?.result || [];
      } else if (data?.rows) {
        rows = data.rows;
      }

      if (rows.length > 0) {
        setTestColumns(Object.keys(rows[0]));
      }
      setTestResults(rows);
      toast.success(`Query returned ${rows.length} rows`);
    } catch (e: any) {
      setTestError(e.message || "Test failed");
      toast.error("Query execution failed");
    } finally {
      setTesting(false);
    }
  };

  // Auto-run test when arriving from widget "View as Table" action
  const autoRunDone = useRef(false);
  useEffect(() => {
    if (autoRun && !autoRunDone.current && rawSql.trim()) {
      autoRunDone.current = true;
      handleTest();
    }
  }, [autoRun, rawSql]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadResults = async () => {
    if (!testResults || testResults.length === 0 || testColumns.length === 0) return;

    // Check daily download limit
    const { allowed, remaining } = await checkAndLog(testResults.length);
    if (!allowed) {
      toast.error(
        `Daily download limit reached. You have used ${dailyLimit.toLocaleString()} of ${dailyLimit.toLocaleString()} rows today. ${remaining.toLocaleString()} rows remaining. Please contact your admin to increase your limit.`,
      );
      return;
    }

    const headers = testColumns.join(",");
    const rows = testResults.map((row) =>
      testColumns
        .map((col) => {
          const val = row[col];
          if (val === null || val === undefined) return "";
          const str = String(val);
          return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str.replace(/"/g, '""')}"` : str;
        })
        .join(","),
    );
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name || "query-results"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Results downloaded as CSV");
  };

  const handleSave = async () => {
    // When saving to DB, name is required; for inline (workflow) mode it's optional
    if (saveToDb && !name.trim()) {
      toast.error("Enter a query name");
      return;
    }
    setSaving(true);
    try {
      if (!saveToDb) {
        // Inline mode – persist to saved_queries (update if existing, insert if new), then return the SQL
        const queryName = name.trim() || "Dashboard Widget Query";
        try {
          const payload: Record<string, any> = {
            name: queryName,
            database_connection_id: connectionId || null,
            sql_query: finalSql,
            generation_mode: tab,
            builder_config: tab === "builder" ? (builder as unknown as Json) : ({} as Json),
            is_report: false,
          };
          let savedId = editingQuery?.id as string | undefined;
          if (editingQuery?.id) {
            await supabase
              .from("saved_queries" as any)
              .update(payload)
              .eq("id", editingQuery.id);
          } else {
            const { data: inserted } = await supabase
              .from("saved_queries" as any)
              .insert(payload)
              .select("id")
              .single();
            savedId = (inserted as any)?.id || "";
          }
          queryClient.invalidateQueries({ queryKey: ["saved-queries"] });
          clearDraft();
          if (onSaved) {
            onSaved({ id: savedId || "", name: queryName, sql_query: finalSql });
          }
          toast.success(editingQuery?.id ? "Query updated" : "Query saved");
        } catch {
          // If DB save fails, still return the SQL to update the widget
          clearDraft();
          if (onSaved) {
            onSaved({ id: editingQuery?.id || "", name: queryName, sql_query: finalSql });
          }
        }
        onClose();
        return;
      }

      const payload: Record<string, any> = {
        name: name.trim(),
        database_connection_id: connectionId || null,
        sql_query: finalSql,
        generation_mode: tab,
        builder_config: tab === "builder" ? (builder as unknown as Json) : ({} as Json),
        is_report: isReport,
      };
      if (linkType) payload.link_type = linkType;
      if (linkId) payload.link_id = linkId;

      let savedId = editingQuery?.id as string | undefined;
      if (editingQuery?.id) {
        const { error } = await supabase
          .from("saved_queries" as any)
          .update(payload)
          .eq("id", editingQuery.id);
        if (error) throw error;
        toast.success("Query updated");
      } else {
        const { data: inserted, error } = await supabase
          .from("saved_queries" as any)
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        savedId = (inserted as any)?.id;
        toast.success("Query saved");
      }
      queryClient.invalidateQueries({ queryKey: ["saved-queries"] });
      clearDraft();
      if (onSaved && savedId) {
        onSaved({ id: savedId, name: name.trim(), sql_query: finalSql });
      }
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Builder helpers
  // All available columns across main table + joined tables
  const allBuilderColumns = useMemo(() => {
    const cols: string[] = [];
    if (builder.table && selectedSchema.columns[builder.table]) {
      selectedSchema.columns[builder.table].forEach((c) => cols.push(`${builder.table}.${c}`));
    }
    for (const j of builder.joins || []) {
      if (j.table && selectedSchema.columns[j.table]) {
        selectedSchema.columns[j.table].forEach((c) => cols.push(`${j.table}.${c}`));
      }
    }
    return cols;
  }, [builder.table, builder.joins, selectedSchema.columns]);

  const addJoin = () =>
    setBuilder((b) => ({
      ...b,
      joins: [...b.joins, { table: "", type: "INNER JOIN" as const, leftColumn: "", rightColumn: "" }],
    }));
  const removeJoin = (i: number) => setBuilder((b) => ({ ...b, joins: b.joins.filter((_, idx) => idx !== i) }));
  const updateJoin = (i: number, field: keyof JoinRow, value: string) =>
    setBuilder((b) => ({ ...b, joins: b.joins.map((j, idx) => (idx === i ? { ...j, [field]: value } : j)) }));

  const addColumnEntry = () =>
    setBuilder((b) => ({ ...b, columns: [...b.columns, { expression: "", alias: "", aggregate: "" as const }] }));
  const removeColumnEntry = (i: number) =>
    setBuilder((b) => ({ ...b, columns: b.columns.filter((_, idx) => idx !== i) }));
  const updateColumnEntry = (i: number, field: keyof ColumnEntry, value: string) =>
    setBuilder((b) => ({ ...b, columns: b.columns.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)) }));

  const addOrderEntry = () =>
    setBuilder((b) => ({ ...b, orderBy: [...b.orderBy, { column: "", direction: "ASC" as const }] }));
  const removeOrderEntry = (i: number) =>
    setBuilder((b) => ({ ...b, orderBy: b.orderBy.filter((_, idx) => idx !== i) }));

  const toggleGroupBy = (col: string) =>
    setBuilder((b) => ({
      ...b,
      groupBy: b.groupBy.includes(col) ? b.groupBy.filter((c) => c !== col) : [...b.groupBy, col],
    }));

  const addFilter = () =>
    setBuilder((b) => ({ ...b, filters: [...b.filters, { column: "", operator: "=", value: "" }] }));
  const removeFilter = (i: number) => setBuilder((b) => ({ ...b, filters: b.filters.filter((_, idx) => idx !== i) }));
  const updateFilter = (i: number, field: keyof FilterRow, value: string) =>
    setBuilder((b) => ({ ...b, filters: b.filters.map((f, idx) => (idx === i ? { ...f, [field]: value } : f)) }));

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-6 py-3 shrink-0 overflow-hidden min-w-0">
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">{editingQuery ? "Edit Query" : "New Query"}</h2>
        <div className="ml-auto flex items-center gap-2">
          {saveToDb && (
            <Button
              variant={isReport ? "default" : "outline"}
              size="sm"
              onClick={() => setIsReport((v) => !v)}
              title={isReport ? "Marked as report" : "Mark as report"}
            >
              <FileText className="mr-1 h-4 w-4" />
              {isReport ? "Report" : "Mark as Report"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setSchemaOpen(true)}>
            <Table2 className="mr-1 h-4 w-4" /> Schema
          </Button>
          <Button variant="outline" size="sm" onClick={handleTest} disabled={testing || !finalSql.trim()}>
            {testing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
            Test
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {saveToDb ? "Save" : "Use Query"}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: Editor */}
        <div className="w-[480px] shrink-0 border-r border-border overflow-y-auto overflow-x-hidden p-6 space-y-4">
          <div>
            <Label>
              Query Name{!saveToDb && <span className="text-muted-foreground font-normal"> (optional label)</span>}
            </Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My query" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full">
              <TabsTrigger value="raw" className="flex-1">
                Raw SQL
              </TabsTrigger>
              <TabsTrigger value="builder" className="flex-1">
                Builder
              </TabsTrigger>
              <TabsTrigger value="ai" className="flex-1">
                AI Generate
              </TabsTrigger>
            </TabsList>

            <TabsContent value="builder" className="space-y-3">
              {/* Main Table */}
              <div>
                <Label>From Table</Label>
                <Select
                  value={builder.table}
                  onValueChange={(v) =>
                    setBuilder((b) => ({ ...b, table: v, columns: [], joins: [], groupBy: [], orderBy: [] }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select table" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedSchema.tables.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Joins */}
              {builder.table && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Joins</Label>
                    <Button size="sm" variant="ghost" onClick={addJoin}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  {builder.joins.map((j, i) => (
                    <div key={i} className="space-y-1 mt-2 p-2 rounded-md border border-border bg-muted/20">
                      <div className="flex gap-2 items-center">
                        <Select value={j.type} onValueChange={(v) => updateJoin(i, "type", v)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {JOIN_TYPES.map((jt) => (
                              <SelectItem key={jt} value={jt}>
                                {jt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={j.table} onValueChange={(v) => updateJoin(i, "table", v)}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Table" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedSchema.tables
                              .filter((t) => t !== builder.table)
                              .map((t) => (
                                <SelectItem key={t} value={t}>
                                  {t}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" onClick={() => removeJoin(i)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="flex gap-2 items-center text-xs">
                        <span className="text-muted-foreground">ON</span>
                        <Select value={j.leftColumn} onValueChange={(v) => updateJoin(i, "leftColumn", v)}>
                          <SelectTrigger className="flex-1 h-8">
                            <SelectValue placeholder="Left col" />
                          </SelectTrigger>
                          <SelectContent>
                            {(selectedSchema.columns[builder.table] || []).map((c) => (
                              <SelectItem key={c} value={`${builder.table}.${c}`}>
                                {builder.table}.{c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-muted-foreground">=</span>
                        <Select value={j.rightColumn} onValueChange={(v) => updateJoin(i, "rightColumn", v)}>
                          <SelectTrigger className="flex-1 h-8">
                            <SelectValue placeholder="Right col" />
                          </SelectTrigger>
                          <SelectContent>
                            {j.table &&
                              (selectedSchema.columns[j.table] || []).map((c) => (
                                <SelectItem key={c} value={`${j.table}.${c}`}>
                                  {j.table}.{c}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Columns */}
              {builder.table && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Columns</Label>
                    <Button size="sm" variant="ghost" onClick={addColumnEntry}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  {builder.columns.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      All columns (*) — add entries to select specific columns
                    </p>
                  )}
                  {builder.columns.map((c, i) => (
                    <div key={i} className="flex gap-1.5 mt-1 items-center">
                      <Select
                        value={c.aggregate || "none"}
                        onValueChange={(v) => updateColumnEntry(i, "aggregate", v === "none" ? "" : v)}
                      >
                        <SelectTrigger className="w-20 h-8 text-xs">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {AGGREGATES.map((a) => (
                            <SelectItem key={a || "none"} value={a || "none"}>
                              {a || "None"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={c.expression} onValueChange={(v) => updateColumnEntry(i, "expression", v)}>
                        <SelectTrigger className="flex-1 h-8 text-xs">
                          <SelectValue placeholder="Column / expr" />
                        </SelectTrigger>
                        <SelectContent>
                          {allBuilderColumns.map((col) => (
                            <SelectItem key={col} value={col}>
                              {col}
                            </SelectItem>
                          ))}
                          <SelectItem value="*">*</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        className="w-24 h-8 text-xs"
                        value={c.alias}
                        onChange={(e) => updateColumnEntry(i, "alias", e.target.value)}
                        placeholder="Alias"
                      />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeColumnEntry(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Filters */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Filters</Label>
                  <Button size="sm" variant="ghost" onClick={addFilter}>
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                {builder.filters.map((f, i) => (
                  <div key={i} className="flex gap-2 mt-1 items-center">
                    <Select value={f.column} onValueChange={(v) => updateFilter(i, "column", v)}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Column" />
                      </SelectTrigger>
                      <SelectContent>
                        {allBuilderColumns.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={f.operator} onValueChange={(v) => updateFilter(i, "operator", v)}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((op) => (
                          <SelectItem key={op} value={op}>
                            {op}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {f.operator !== "IS NULL" && f.operator !== "IS NOT NULL" && (
                      <Input
                        className="flex-1"
                        value={f.value}
                        onChange={(e) => updateFilter(i, "value", e.target.value)}
                        placeholder="Value"
                      />
                    )}
                    <Button size="icon" variant="ghost" onClick={() => removeFilter(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Group By */}
              {builder.table && allBuilderColumns.length > 0 && (
                <div>
                  <Label>Group By</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {allBuilderColumns.map((col) => (
                      <label key={col} className="flex items-center gap-1.5 text-xs">
                        <Checkbox checked={builder.groupBy.includes(col)} onCheckedChange={() => toggleGroupBy(col)} />
                        {col}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Order By */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Order By</Label>
                  <Button size="sm" variant="ghost" onClick={addOrderEntry}>
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                {builder.orderBy.map((o, i) => (
                  <div key={i} className="flex gap-2 mt-1 items-center">
                    <Select
                      value={o.column}
                      onValueChange={(v) =>
                        setBuilder((b) => ({
                          ...b,
                          orderBy: b.orderBy.map((x, idx) => (idx === i ? { ...x, column: v } : x)),
                        }))
                      }
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Column" />
                      </SelectTrigger>
                      <SelectContent>
                        {allBuilderColumns.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={o.direction}
                      onValueChange={(v) =>
                        setBuilder((b) => ({
                          ...b,
                          orderBy: b.orderBy.map((x, idx) =>
                            idx === i ? { ...x, direction: v as "ASC" | "DESC" } : x,
                          ),
                        }))
                      }
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ASC">ASC</SelectItem>
                        <SelectItem value="DESC">DESC</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" onClick={() => removeOrderEntry(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Limit */}
              <div className="w-24">
                <Label>Limit</Label>
                <Input value={builder.limit} onChange={(e) => setBuilder((b) => ({ ...b, limit: e.target.value }))} />
              </div>
            </TabsContent>

            <TabsContent value="ai" className="space-y-3">
              <div>
                <Label>Describe your query</Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g. Get the top 10 users by order count…"
                  rows={3}
                />
              </div>
              <Button onClick={handleGenerate} disabled={generating} size="sm">
                {generating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
                Generate SQL
              </Button>
              {aiSql && (
                <div>
                  <Label>Generated SQL (editable)</Label>
                  <SqlTextarea
                    value={aiSql}
                    onChange={setAiSql}
                    tables={selectedSchema.tables}
                    columns={selectedSchema.columns}
                    rows={6}
                    schemaWarnings={schemaWarnings}
                  />
                  {schemaWarnings.length > 0 && (
                    <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-amber-500 text-xs font-medium">
                          <AlertCircle className="h-3.5 w-3.5" />
                          Schema issues found
                        </div>
                        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={handleFixQuery} disabled={fixingQuery}>
                          {fixingQuery ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                          Fix Query
                        </Button>
                      </div>
                      {schemaWarnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-amber-400/90">
                          <span className="shrink-0 mt-0.5 rounded px-1 py-0.5 bg-amber-500/10 font-mono text-[10px]">
                            {w.type === "table" ? "TABLE" : "COLUMN"}
                          </span>
                          <span>{w.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="raw" className="space-y-3">
              <div>
                <Label>SQL Query</Label>
                <SqlTextarea
                  value={rawSql}
                  onChange={setRawSql}
                  tables={selectedSchema.tables}
                  columns={selectedSchema.columns}
                  placeholder="SELECT * FROM …"
                  rows={8}
                  schemaWarnings={schemaWarnings}
                />
                {schemaWarnings.length > 0 && (
                  <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-amber-500 text-xs font-medium">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Schema issues found
                      </div>
                      <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" onClick={handleFixQuery} disabled={fixingQuery}>
                        {fixingQuery ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        Fix Query
                      </Button>
                    </div>
                    {schemaWarnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-amber-400/90">
                        <span className="shrink-0 mt-0.5 rounded px-1 py-0.5 bg-amber-500/10 font-mono text-[10px]">
                          {w.type === "table" ? "TABLE" : "COLUMN"}
                        </span>
                        <span>{w.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Filter / Placeholder Syntax Guide */}
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 h-7"
              >
                <FileText className="h-3.5 w-3.5" />
                How to add filters / parameters
                <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="rounded-md border border-border bg-muted/50 p-4 mt-1 space-y-3 text-xs text-muted-foreground">
                <p className="font-medium text-foreground text-sm">Filter Syntax Guide</p>
                <p>
                  Use{" "}
                  <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-[11px] text-foreground">
                    {"{{name}}"}
                  </code>{" "}
                  placeholders in your SQL to create dynamic filters. When a query contains placeholders, users will see
                  input fields to fill in values before running.
                </p>

                <div className="space-y-1">
                  <p className="font-medium text-foreground">Syntax</p>
                  <code className="block bg-muted px-2 py-1 rounded font-mono text-[11px]">
                    {"{{name}}"} — basic text input
                  </code>
                  <code className="block bg-muted px-2 py-1 rounded font-mono text-[11px]">
                    {"{{name|type}}"} — specify input type
                  </code>
                  <code className="block bg-muted px-2 py-1 rounded font-mono text-[11px]">
                    {"{{name|type|default}}"} — with default value
                  </code>
                </div>

                <div className="space-y-1">
                  <p className="font-medium text-foreground">Available Types</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        text
                      </Badge>
                      <span>Free text input (default)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        number
                      </Badge>
                      <span>Numeric input</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        date
                      </Badge>
                      <span>Date picker</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        datetime-local
                      </Badge>
                      <span>Date &amp; time picker</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        dropdown
                      </Badge>
                      <span>Single-select from column values</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        multiselect
                      </Badge>
                      <span>Multi-select (expands to IN list)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                        autocomplete
                      </Badge>
                      <span>Searchable dropdown from column values</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="font-medium text-foreground">Examples</p>
                  <pre className="bg-muted px-3 py-2 rounded font-mono text-[11px] whitespace-pre-wrap leading-relaxed">{`-- Text filter
SELECT * FROM orders WHERE customer_name = {{customer}}

-- Dropdown filter with default value
SELECT * FROM flights WHERE origin = {{origin|dropdown|JFK}}

-- Date filter with "today" default
SELECT * FROM sales WHERE sale_date >= {{start_date|date|today}}

-- Multiselect filter (expands to IN list)
SELECT * FROM products WHERE category IN ({{categories|multiselect}})

-- Number filter
SELECT * FROM items WHERE price > {{min_price|number|0}}

-- Datetime filter with "now" default
SELECT * FROM logs WHERE created_at >= {{from_time|datetime-local|now}}

-- Autocomplete for large value lists
SELECT * FROM users WHERE city = {{city|autocomplete}}`}</pre>
                </div>

                <div className="space-y-1">
                  <p className="font-medium text-foreground">Date Defaults</p>
                  <p>
                    For <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">date</code> and{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">datetime-local</code> types,
                    use special keywords:{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">now</code>,{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">today</code>,{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">yesterday</code>,{" "}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">tomorrow</code>
                  </p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Schema Validation Warnings */}
          {schemaWarnings.length > 0 && (
            <div className="rounded-md border border-yellow-500/40 bg-yellow-500/5 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span className="text-xs font-semibold">Schema Warnings ({schemaWarnings.length})</span>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={handleFixQuery} disabled={fixingQuery}>
                  {fixingQuery ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  Fix Query
                </Button>
              </div>
              <ul className="space-y-1">
                {schemaWarnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <span
                      className={cn(
                        "shrink-0 rounded px-1 py-0.5 text-[10px] font-medium uppercase mt-0.5",
                        w.type === "table"
                          ? "bg-destructive/15 text-destructive"
                          : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
                      )}
                    >
                      {w.type}
                    </span>
                    <span className="font-mono text-foreground">{w.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* SQL Preview */}
          {finalSql && (
            <div>
              <Label>SQL Preview</Label>
              <pre className="rounded-md border border-border bg-muted p-3 text-xs font-mono whitespace-pre-wrap">
                {finalSql}
              </pre>
            </div>
          )}

          {/* Placeholder Parameters */}
          {placeholderInfos.length > 0 && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-3">
              <Label className="text-sm font-semibold">Query Parameters</Label>
              <p className="text-xs text-muted-foreground">
                Fill in the values below before running. Use{" "}
                <code className="bg-muted px-1 rounded text-[10px]">{"{{name|type}}"}</code> to set input type and{" "}
                <code className="bg-muted px-1 rounded text-[10px]">{"{{name|type|default}}"}</code> to set a default
                value. Date supports <code className="bg-muted px-1 rounded text-[10px]">now</code>,{" "}
                <code className="bg-muted px-1 rounded text-[10px]">today</code>,{" "}
                <code className="bg-muted px-1 rounded text-[10px]">yesterday</code>,{" "}
                <code className="bg-muted px-1 rounded text-[10px]">tomorrow</code>.
              </p>
              {placeholderInfos.map((ph) => (
                <div key={ph.name}>
                  <Label className="text-xs">
                    {ph.name}
                    {ph.columnName && (
                      <span className="ml-1 text-muted-foreground font-normal">
                        ({ph.columnName}
                        {ph.columnType ? ` : ${ph.columnType}` : ""})
                      </span>
                    )}
                    <span className="ml-1 text-muted-foreground font-normal text-[10px]">[{ph.displayType}]</span>
                  </Label>
                  <div className="mt-1">
                    <PlaceholderInput
                      placeholder={ph}
                      value={placeholderValues[ph.name] ?? ""}
                      onChange={(v) => setPlaceholderValues((prev) => ({ ...prev, [ph.name]: v }))}
                      sql={finalSql}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Test Results */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden w-0">
          <div className="flex items-center gap-2 border-b border-border px-4 py-2">
            <span className="text-sm font-medium">Test Results</span>
            {testResults && (
              <span className="text-xs text-muted-foreground">
                {testResults.length} row{testResults.length !== 1 ? "s" : ""}
              </span>
            )}
            {testResults && testResults.length > 0 && downloadAllowed && (
              <>
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {todayUsed.toLocaleString()}/{dailyLimit.toLocaleString()} rows today
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleDownloadResults}
                  disabled={downloadRemaining <= 0}
                  title={downloadRemaining <= 0 ? "Daily download limit reached. Contact your admin." : undefined}
                >
                  <Download className="mr-1 h-3.5 w-3.5" /> Download CSV
                </Button>
              </>
            )}
          </div>
          {/* Download limit reached banner */}
          {downloadAllowed && testResults && testResults.length > 0 && downloadRemaining <= 0 && (
            <div className="flex items-center gap-2 border-b border-destructive/50 bg-destructive/10 px-4 py-2 text-xs text-destructive">
              <span>
                Daily download limit reached ({dailyLimit.toLocaleString()} rows). Please contact your admin to increase
                your limit.
              </span>
            </div>
          )}

          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {testing && (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {testError && !testing && (
              <div className="p-6 flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-5 w-5 shrink-0" />
                  <span className="text-sm font-medium">Query Execution Error</span>
                </div>
                <pre className="w-full max-w-2xl rounded-md border border-destructive/30 bg-destructive/5 p-4 text-xs font-mono text-destructive whitespace-pre-wrap break-words overflow-auto max-h-48">
                  {testError}
                </pre>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(testError);
                    toast.success("Error copied to clipboard");
                  }}
                >
                  Copy Error
                </Button>
              </div>
            )}

            {!testing && !testError && !testResults && (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
                <Play className="h-8 w-8" />
                <p className="text-sm">Click "Test" to run the query</p>
                <p className="text-xs">Results are limited to 1,000 rows</p>
              </div>
            )}

            {!testing && testResults && testResults.length === 0 && (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <p className="text-sm">Query returned no results</p>
              </div>
            )}

            {!testing && testResults && testResults.length > 0 && (
              <div className="overflow-auto flex-1 min-h-0 relative">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-muted">
                      {testColumns.map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left font-medium text-muted-foreground border-b border-border whitespace-nowrap bg-muted"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30">
                        {testColumns.map((col) => (
                          <td key={col} className="px-3 py-1.5 whitespace-nowrap max-w-[300px] truncate">
                            {row[col] === null ? (
                              <span className="text-muted-foreground italic">null</span>
                            ) : (
                              String(row[col])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={schemaOpen} onOpenChange={setSchemaOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 className="h-5 w-5" />
              Database Schema
              {schemaData.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {schemaData.length} {schemaData.length === 1 ? "table" : "tables"}
                </Badge>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 ml-auto"
                disabled={schemaLoading}
                onClick={() => refetchSchema()}
                title="Refresh schema"
              >
                <RefreshCw className={cn("h-4 w-4", schemaLoading && "animate-spin")} />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <input
            type="text"
            value={schemaSearch}
            onChange={(e) => setSchemaSearch(e.target.value)}
            placeholder="Search tables or columns…"
            className="h-8 w-full rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <div className="flex-1 overflow-y-auto space-y-2 mt-2">
            {schemaLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : schemaData.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground pt-10">No schema available.</p>
            ) : (
              schemaData
                .filter(
                  (t) =>
                    t.table_name.toLowerCase().includes(schemaSearch.toLowerCase()) ||
                    t.columns.some((c) => c.column_name.toLowerCase().includes(schemaSearch.toLowerCase())),
                )
                .map((table) => {
                  const isOpen = expandedTables.has(table.table_name);
                  return (
                    <Collapsible
                      key={table.table_name}
                      open={isOpen}
                      onOpenChange={() => {
                        setExpandedTables((prev) => {
                          const next = new Set(prev);
                          if (next.has(table.table_name)) next.delete(table.table_name);
                          else next.add(table.table_name);
                          return next;
                        });
                      }}
                    >
                      <CollapsibleTrigger asChild>
                        <button className="flex w-full items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-left transition-colors hover:bg-accent">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <Table2 className="h-4 w-4 shrink-0 text-primary" />
                          <span className="flex-1 text-sm font-medium">{table.table_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {table.columns.length} col{table.columns.length !== 1 ? "s" : ""}
                          </Badge>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mx-1 mb-1 overflow-hidden rounded-b-lg border border-t-0 border-border">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/40">
                                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                                  Column
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Type</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                                  Nullable
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {table.columns.map((col, idx) => (
                                <tr
                                  key={col.column_name}
                                  className={cn(
                                    "border-b border-border last:border-0",
                                    idx % 2 === 0 ? "bg-background" : "bg-muted/20",
                                  )}
                                >
                                  <td className="flex items-center gap-2 px-4 py-2">
                                    <Columns className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="font-mono text-xs">{col.column_name}</span>
                                  </td>
                                  <td className="px-4 py-2">
                                    <Badge variant="secondary" className="font-mono text-xs">
                                      {col.data_type}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-2 text-xs text-muted-foreground">
                                    {col.is_nullable === "YES" ||
                                    col.is_nullable === "true" ||
                                    col.is_nullable === undefined
                                      ? "Yes"
                                      : "No"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QueryEditor;
